"use client";

import { useState, useEffect } from "react";
import Icon from "@/components/ui/AppIcon";
import { trackEvent } from "@/lib/analytics";

export default function ContactForm() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    inquiryType: "",
    budgetRange: "",
    message: "",
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isHydrated || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        trackEvent('form_submission', {
          form_name: 'contact_form',
          inquiry_type: formData.inquiryType,
          budget_range: formData.budgetRange,
          has_company: !!formData.company,
          lead_quality: 'qualified'
        });

        setSubmitStatus({
          type: 'success',
          message: 'Thank you for your inquiry! Our team will contact you within 24 hours.',
        });
        setFormData({ name: '', email: '', company: '', phone: '', inquiryType: '', budgetRange: '', message: '' });
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.error || 'Failed to send message. Please try again.',
        });
      }
    } catch {
      setSubmitStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    if (!isHydrated) return;

    const { name, value } = e.target;

    if (name === 'inquiryType' && value) {
      trackEvent('inquiry_type_selected', {
        inquiry_type: value,
        funnel_stage: 'lead_qualification'
      });
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  return (
    <section className="py-12 sm:py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">

          {/* Left Content */}
          <div>
            <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="w-[2px] h-4 bg-gradient-to-b from-golden to-silver flex-shrink-0" />
              <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.25em] sm:tracking-[0.3em] font-bold text-muted-foreground font-mono">
                Contact Information
              </span>
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-4 sm:mb-6 font-heading leading-tight">
              Start Your Partnership Journey
            </h2>

            <p className="text-sm sm:text-base lg:text-lg text-muted-foreground mb-8 sm:mb-10 font-sans leading-relaxed">
              Fill out the form and our team will get back to you with a detailed quote for your mineral sourcing needs.
            </p>

            {/* Contact Info */}
            <div className="space-y-5 sm:space-y-6">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-golden to-silver rounded-full flex items-center justify-center text-foreground flex-shrink-0">
                  <Icon name="MapPinIcon" size={18} />
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-semibold text-foreground mb-1 font-heading">
                    Head Office
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground font-sans">
                    Plot 42, Industrial Area<br />
                    Kampala, Uganda
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-silver to-golden rounded-full flex items-center justify-center text-foreground flex-shrink-0">
                  <Icon name="EnvelopeIcon" size={18} />
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-semibold text-foreground mb-1 font-heading">
                    Email
                  </h4>
                  <a
                    href="mailto:mineralsethical@gmail.com"
                    className="text-xs sm:text-sm text-primary hover:text-primary/80 transition-colors font-sans break-all"
                  >
                    mineralsethical@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-golden to-silver rounded-full flex items-center justify-center text-foreground flex-shrink-0">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-semibold text-foreground mb-1 font-heading">
                    WhatsApp / Telegram
                  </h4>
                  <a
                    href="https://wa.me/254729286669"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs sm:text-sm text-primary hover:text-primary/80 transition-colors font-sans block"
                  >
                    +254 729 286669 (WhatsApp)
                  </a>
                  <a
                    href="https://t.me/+254729286669"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs sm:text-sm text-primary hover:text-primary/80 transition-colors font-sans block"
                  >
                    +254 729 286669 (Telegram)
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Form */}
          <div className="bg-card border-2 border-silver/30 rounded-2xl p-5 sm:p-6 md:p-8 shadow-xl">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 md:space-y-6">

              {/* Name + Email row on md+ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-foreground mb-2 font-mono"
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-background border-2 border-border rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm text-foreground focus:outline-none focus:border-golden transition-colors font-sans"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-foreground mb-2 font-mono"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-background border-2 border-border rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm text-foreground focus:outline-none focus:border-golden transition-colors font-sans"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              {/* Company + Phone row on md+ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <div>
                  <label
                    htmlFor="company"
                    className="block text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-foreground mb-2 font-mono"
                  >
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full bg-background border-2 border-border rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm text-foreground focus:outline-none focus:border-golden transition-colors font-sans"
                    placeholder="Your Company Ltd"
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-foreground mb-2 font-mono"
                  >
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full bg-background border-2 border-border rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm text-foreground focus:outline-none focus:border-golden transition-colors font-sans"
                    placeholder="+256 700 000 000"
                  />
                </div>
              </div>

              {/* Inquiry Type + Budget row on md+ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <div>
                  <label
                    htmlFor="inquiryType"
                    className="block text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-foreground mb-2 font-mono"
                  >
                    Inquiry Type *
                  </label>
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    required
                    className="w-full bg-background border-2 border-border rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm text-foreground focus:outline-none focus:border-golden transition-colors font-sans"
                  >
                    <option value="">Select inquiry type</option>
                    <option value="Sample Request">Sample Request</option>
                    <option value="Bulk Order">Bulk Order</option>
                    <option value="Partnership">Partnership</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="budgetRange"
                    className="block text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-foreground mb-2 font-mono"
                  >
                    Budget Range *
                  </label>
                  <select
                    id="budgetRange"
                    name="budgetRange"
                    value={formData.budgetRange}
                    onChange={handleChange}
                    required
                    className="w-full bg-background border-2 border-border rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm text-foreground focus:outline-none focus:border-golden transition-colors font-sans"
                  >
                    <option value="">Select budget range</option>
                    <option value="Under $10,000">Under $10,000</option>
                    <option value="$10,000 - $50,000">$10,000 - $50,000</option>
                    <option value="$50,000 - $100,000">$50,000 - $100,000</option>
                    <option value="$100,000 - $500,000">$100,000 - $500,000</option>
                    <option value="Over $500,000">Over $500,000</option>
                  </select>
                </div>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-foreground mb-2 font-mono"
                >
                  Mineral Quote Request *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full bg-background border-2 border-border rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm text-foreground focus:outline-none focus:border-golden transition-colors resize-none font-sans"
                  placeholder="Tell us about your mineral requirements, quantities, and specifications..."
                />
              </div>

              {submitStatus.type && (
                <div
                  className={`p-3 sm:p-4 rounded-lg ${
                    submitStatus.type === 'success' ?'bg-green-50 border-2 border-green-200 text-green-800' :'bg-red-50 border-2 border-red-200 text-red-800'
                  }`}
                >
                  <p className="text-xs sm:text-sm font-sans">{submitStatus.message}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-golden via-silver to-golden text-foreground py-3 sm:py-4 rounded-lg text-xs sm:text-sm font-semibold uppercase tracking-widest hover:opacity-90 transition-all flex items-center justify-center gap-2 group font-mono shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{isSubmitting ? 'Sending...' : 'Send Inquiry'}</span>
                <Icon
                  name="PaperAirplaneIcon"
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}